import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2, UserCircle, RefreshCw } from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type UserData = {
  id: string;
  email: string;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  roles: string[];
};

type Role = "user" | "manager" | "admin" | "super_admin";

const UserManagement = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role>("user");
  const [isUpdating, setIsUpdating] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      
      // Fetch profiles from the public.profiles table instead of auth.users
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, email, created_at, first_name, last_name");
      
      if (profilesError) throw profilesError;
      
      if (!profilesData) {
        setUsers([]);
        return;
      }
      
      // For each profile, fetch their roles
      const usersWithRoles = await Promise.all(
        profilesData.map(async (profile) => {
          // Fetch user roles
          const { data: roleData } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", profile.id);
          
          return {
            id: profile.id,
            email: profile.email,
            created_at: profile.created_at,
            first_name: profile.first_name,
            last_name: profile.last_name,
            roles: roleData ? roleData.map((r) => r.role) : [],
          };
        })
      );
      
      setUsers(usersWithRoles);
    } catch (error: any) {
      console.error("Error fetching users:", error);
      toast({
        title: "Failed to load users",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchUsers();
    setIsRefreshing(false);
    toast({
      title: "User list refreshed",
      description: "The user list has been updated with the latest data."
    });
  };

  const handleRoleChange = async () => {
    if (!selectedUser) return;
    
    try {
      setIsUpdating(true);
      
      // First check if user already has this role
      const userHasRole = selectedUser.roles.includes(selectedRole);
      
      if (!userHasRole) {
        // Add the new role
        const { error: insertError } = await supabase
          .from("user_roles")
          .insert({
            user_id: selectedUser.id,
            role: selectedRole,
          });
        
        if (insertError) throw insertError;
        
        toast({
          title: "Role added",
          description: `Added ${selectedRole} role to ${selectedUser.email}`,
        });
      } else {
        toast({
          title: "Role already assigned",
          description: `User already has the ${selectedRole} role`,
        });
      }
      
      // Refresh the user list
      await fetchUsers();
    } catch (error: any) {
      console.error("Error updating role:", error);
      toast({
        title: "Failed to update role",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };
  
  const removeRole = async (userId: string, role: string) => {
    try {
      setIsUpdating(true);
      
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .match({ user_id: userId, role });
      
      if (error) throw error;
      
      toast({
        title: "Role removed",
        description: `Removed ${role} role successfully`,
      });
      
      // Refresh the user list
      await fetchUsers();
    } catch (error: any) {
      console.error("Error removing role:", error);
      toast({
        title: "Failed to remove role",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getUserName = (user: UserData) => {
    if (user.first_name && user.last_name) return `${user.first_name} ${user.last_name}`;
    if (user.first_name) return user.first_name;
    if (user.last_name) return user.last_name;
    return "User";
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">User Management</h2>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={isRefreshing || isLoading}
          className="flex items-center gap-2"
        >
          {isRefreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh Users
        </Button>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <h3 className="text-lg font-medium">Users</h3>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <UserCircle className="h-6 w-6 text-muted-foreground" />
                        <span>{getUserName(user)}</span>
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{formatDate(user.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.roles && user.roles.length > 0 ? (
                          user.roles.map((role, index) => (
                            <div key={index} className="flex items-center gap-1">
                              <Badge 
                                variant={
                                  role === "super_admin" 
                                    ? "destructive" 
                                    : role === "admin" 
                                    ? "default" 
                                    : "secondary"
                                }
                                className="text-xs"
                              >
                                {role}
                              </Badge>
                              
                              {/* Don't allow removing the only role */}
                              {user.roles.length > 1 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-4 w-4 p-0 text-muted-foreground hover:text-foreground"
                                  onClick={() => removeRole(user.id, role)}
                                  disabled={isUpdating}
                                >
                                  ×
                                </Button>
                              )}
                            </div>
                          ))
                        ) : (
                          <Badge variant="outline" className="text-xs">
                            No roles
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedUser(user)}
                          >
                            Add Role
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Add Role to User</DialogTitle>
                          </DialogHeader>
                          <div className="py-4">
                            <p className="mb-4">
                              Adding role to: <strong>{user.email}</strong>
                            </p>
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <label htmlFor="role" className="text-sm font-medium">
                                  Select Role
                                </label>
                                <Select
                                  value={selectedRole}
                                  onValueChange={(value) => 
                                    setSelectedRole(value as Role)
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select a role" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="user">User</SelectItem>
                                    <SelectItem value="manager">Manager</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    <SelectItem value="super_admin">
                                      Super Admin
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <Button
                                onClick={handleRoleChange}
                                disabled={isUpdating}
                                className="w-full"
                              >
                                {isUpdating ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Adding...
                                  </>
                                ) : (
                                  "Add Role"
                                )}
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UserManagement;
